import React, { useRef, useState } from 'react';
import { useTweaks } from './TweaksPanel';
import { LangProvider } from './LangContext';
import ThemePanel from './components/ThemePanel';
import { useSectionScroll } from './hooks/useSectionScroll';
import { useAmbientSound } from './hooks/useAmbientSound';
import { useLiveWeather } from './hooks/useLiveWeather';
import Nav from './components/Nav';
import Hero from './components/Hero';
import About from './components/About';
import Stack from './components/Stack';
import Timeline from './components/Timeline';
import Projects from './components/Projects';
import Stats from './components/Stats';
import Services from './components/Services';
import Contact from './components/Contact';
import SectionDots from './components/SectionDots';
import MobileScrollUp from './components/MobileScrollUp';
import Eyes from './components/Eyes';
import RainCanvas from './components/RainCanvas';

const TWEAK_DEFAULTS = {
  palette: 'ci',
  dark: false,
};

export default function App() {
  const haloRef = useRef(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const { active, total, goTo } = useSectionScroll();
  const { enabled: soundOn, toggle: toggleSound } = useAmbientSound();
  const weather = useLiveWeather();
  const liveRain = !!weather?.raining;

  React.useLayoutEffect(() => {
    document.documentElement.dataset.palette = t.palette;
    document.documentElement.dataset.theme = t.dark ? 'dark' : 'light';
    // Keep the mobile browser chrome in sync with the actual background.
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
    if (bg) document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
  }, [t.palette, t.dark]);

  // Terminal / badge actions (rain, theme) — decoupled via a window event so
  // deep components don't need prop drilling.
  React.useEffect(() => {
    const onAction = (e) => {
      const { type, value } = e.detail || {};
      if (type === 'rain') {
        if (value === 'on' && !soundOn) toggleSound();
        else if (value === 'off' && soundOn) toggleSound();
        else if (value === 'toggle') toggleSound();
      } else if (type === 'theme') {
        setTweak('dark', value === 'dark');
      }
    };
    window.addEventListener('portfolio:action', onAction);
    return () => window.removeEventListener('portfolio:action', onAction);
  }, [soundOn, toggleSound, setTweak]);

  // Cursor halo — pointer devices only; the loop stops as soon as the halo
  // has caught up with the cursor instead of running forever.
  React.useEffect(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0, running = false;
    let tx = -200, ty = -200, x = -200, y = -200;

    const animate = () => {
      x += (tx - x) * 0.15;
      y += (ty - y) * 0.15;
      if (haloRef.current) {
        haloRef.current.style.left = x + 'px';
        haloRef.current.style.top = y + 'px';
      }
      if (Math.abs(tx - x) < 0.3 && Math.abs(ty - y) < 0.3) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(animate);
    };

    const move = (e) => {
      tx = e.clientX;
      ty = e.clientY;
      if (!running) {
        running = true;
        raf = requestAnimationFrame(animate);
      }
    };

    window.addEventListener('mousemove', move, { passive: true });
    return () => {
      window.removeEventListener('mousemove', move);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <LangProvider>
      <div className="halo" ref={haloRef} />
      <RainCanvas
        active={soundOn || liveRain}
        soft={liveRain && !soundOn}
        dark={t.dark}
        sectionIndex={active}
        weatherBoost={liveRain ? (weather.heavy ? 0.35 : 0.18) : 0}
      />
      <Eyes />
      <Nav onTweaks={() => setPanelOpen((v) => !v)} panelOpen={panelOpen} active={active} soundOn={soundOn} toggleSound={toggleSound} />
      <div id="sections-container">
        <Hero liveRain={liveRain} soundOn={soundOn} toggleSound={toggleSound} />
        <About />
        <Stack />
        <Timeline />
        <Projects />
        <Stats />
        <Services />
        <Contact />
      </div>
      <SectionDots total={total} active={active} goTo={goTo} />
      <MobileScrollUp active={active} goTo={goTo} />
      <ThemePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        palette={t.palette}
        dark={t.dark}
        setTweak={setTweak}
      />
    </LangProvider>
  );
}
